import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDateRangePresets, useReportsData } from '../hooks';
import { 
  ReportsFilters, 
  KPIDashboard 
} from '../components/reports';
import { FileText } from 'lucide-react';

const Reports: React.FC = () => {
  const { user } = useAuth();
  
  // ✅ Step 1: Use centralized date logic
  const { 
    getPresetRange, 
    getComparisonPeriod,
    getComparisonPresetMapping 
  } = useDateRangePresets();
  
  const currentYear = new Date().getFullYear();
  
  // Initialize with default range (last 2 weeks)
  const defaultPeriod = useMemo(() => 
    getPresetRange('last2Weeks') || { startDay: '', endDay: '' }, 
    [getPresetRange]
  );
  const defaultComparison = useMemo(() => 
    getPresetRange('previous2Weeks') || { startDay: '', endDay: '' }, 
    [getPresetRange]
  );
  
  // Local state for selected ranges
  const [dateRange, setDateRange] = useState(defaultPeriod);
  const [compareToRange, setCompareToRange] = useState(defaultComparison);
  const [selectedPreset, setSelectedPreset] = useState<string>('last2Weeks');
  const [selectedComparePreset, setSelectedComparePreset] = useState<string>('previous2Weeks');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedCompareYear, setSelectedCompareYear] = useState<number>(currentYear - 1);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);
  
  // ✅ Step 2: Use consolidated data fetching
  const { state, actions, loading, hasErrors, errors } = useReportsData(compareToRange);
  
  // ✅ Step 3: Simplified event handlers
  const handlePresetChange = async (preset: string) => {
    setSelectedPreset(preset);
    setIsApplyingPreset(true);

    const newRange = getPresetRange(preset, selectedYear);
    if (!newRange) {
      setIsApplyingPreset(false);
      return;
    }

    setDateRange(newRange);

    // Smart prefill for Compare To
    const comparePreset = getComparisonPresetMapping(preset);
    setSelectedComparePreset(comparePreset);
    
    // Calculate compare year based on preset type
    let compareYear = selectedYear;
    if (['q1', 'q2', 'q3', 'q4', '1h', '2h', 'cy'].includes(preset)) {
      compareYear = selectedYear - 1;
      setSelectedCompareYear(compareYear);
    }
    
    const compareRange = getPresetRange(comparePreset, compareYear);
    if (compareRange) {
      setCompareToRange(compareRange);
    }

    // Auto-fetch data with new range
    try {
      await Promise.all([
        actions.fetchWellbeing({ startDay: newRange.startDay, endDay: newRange.endDay }),
        actions.fetchDau({ startDate: newRange.startDay, endDate: newRange.endDay }),
        actions.fetchFeedback({ 
          startDay: newRange.startDay, 
          endDay: newRange.endDay, 
          includeAllVersions: true 
        })
      ]);
    } finally {
      setIsApplyingPreset(false);
    }
  };

  const handleYearChange = async (year: number) => {
    setSelectedYear(year);
    setIsApplyingPreset(true);

    const newRange = getPresetRange(selectedPreset, year);
    if (!newRange) {
      setIsApplyingPreset(false);
      return;
    }

    setDateRange(newRange);

    // Update compare year (previous year for year-based periods)
    if (['q1', 'q2', 'q3', 'q4', '1h', '2h', 'cy'].includes(selectedPreset)) {
      const compareYear = year - 1;
      setSelectedCompareYear(compareYear);
      const compareRange = getPresetRange(selectedComparePreset, compareYear);
      if (compareRange) {
        setCompareToRange(compareRange);
      }
    }

    // Auto-fetch data with new range
    try {
      await Promise.all([
        actions.fetchWellbeing({ startDay: newRange.startDay, endDay: newRange.endDay }),
        actions.fetchDau({ startDate: newRange.startDay, endDate: newRange.endDay }),
        actions.fetchFeedback({ 
          startDay: newRange.startDay, 
          endDay: newRange.endDay, 
          includeAllVersions: true 
        })
      ]);
    } finally {
      setIsApplyingPreset(false);
    }
  };

  const handleComparePresetChange = (preset: string) => {
    setSelectedComparePreset(preset);
    
    if (preset !== 'custom') {
      const newRange = getPresetRange(preset, selectedCompareYear);
      if (newRange) {
        setCompareToRange(newRange);
      }
    }
  };

  const handleCompareYearChange = (year: number) => {
    setSelectedCompareYear(year);
    
    const newRange = getPresetRange(selectedComparePreset, year);
    if (newRange) {
      setCompareToRange(newRange);
    }
  };

  const handleDateRangeChange = (field: 'startDay' | 'endDay', value: string) => {
    if (!value) return;

    const newDateRange = { ...dateRange, [field]: value };
    
    // Validate dates
    if (field === 'endDay' && value < newDateRange.startDay) {
      console.warn('End date cannot be before start date');
      return;
    }
    if (field === 'startDay' && value > newDateRange.endDay) {
      console.warn('Start date cannot be after end date');
      return;
    }

    setDateRange(newDateRange);
    setSelectedPreset('custom');

    // Update comparison period automatically to previous period
    if (newDateRange.startDay && newDateRange.endDay) {
      const comparison = getComparisonPeriod(newDateRange.startDay, newDateRange.endDay);
      if (comparison.startDay && comparison.endDay) {
        setCompareToRange({ startDay: comparison.startDay, endDay: comparison.endDay });
        setSelectedComparePreset('custom');
      }
    }
  };

  const handleCompareToRangeChange = (field: 'startDay' | 'endDay', value: string) => {
    if (!value) return;

    const newCompareToRange = { ...compareToRange, [field]: value };
    
    // Validate dates
    if (field === 'endDay' && value < newCompareToRange.startDay) {
      console.warn('Comparison end date cannot be before start date');
      return;
    }
    if (field === 'startDay' && value > newCompareToRange.endDay) {
      console.warn('Comparison start date cannot be after end date');
      return;
    }

    setCompareToRange(newCompareToRange);
    setSelectedComparePreset('custom');
  };

  const handleApplyFilter = async () => {
    setIsApplyingPreset(true);
    
    try {
      // Fetch all data with new date range
      await Promise.all([
        actions.fetchWellbeing({ startDay: dateRange.startDay, endDay: dateRange.endDay }),
        actions.fetchDau({ startDate: dateRange.startDay, endDate: dateRange.endDay }),
        actions.fetchFeedback({ 
          startDay: dateRange.startDay, 
          endDay: dateRange.endDay, 
          includeAllVersions: true 
        })
      ]);
    } finally {
      setIsApplyingPreset(false);
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    if (user?.roles?.includes('reportsReader')) {
      handleApplyFilter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const hasAccess = Boolean(user?.roles?.includes('reportsReader'));

  // Access denied screen
  if (!hasAccess) {
    return (
      <div className="h-full overflow-y-auto bg-rm-bg-0">
        <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="rounded-card border border-red-200 bg-white p-10 text-center shadow-card">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-600">
              <FileText className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl font-semibold text-rm-ink-900">Access restricted</h2>
            <p className="mt-3 text-sm text-rm-neutral-600">
              You need the <span className="font-medium text-rm-ink-900">reportsReader</span> role to view enterprise analytics. Please
              contact your administrator to request access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Step 4: Clean, modular render
  return (
    <div className="h-full overflow-y-auto bg-rm-bg-0">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header Section */}
        <section className="mb-10">
          <div className="relative overflow-hidden rounded-card border border-rm-neutral-600/20 bg-rm-primary-700 shadow-card">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,_rgba(245,219,145,0.3),_transparent_60%)]" />
            <div className="relative p-8 md:p-10">
              <div className="space-y-5">
                <div>
                  <h1 className="text-3xl font-semibold leading-tight text-white md:text-4xl">
                    EMPLOYEE ASSISTANCE PROGRAM (EAP) DASHBOARD
                  </h1>
                  <p className="mt-3 text-base text-rm-accent-300">
                    Monitor wellbeing health, engagement, and satisfaction indexes across your organisation in real time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ✅ Filters Component - replaces 200+ lines */}
        <ReportsFilters
          dateRange={dateRange}
          compareToRange={compareToRange}
          selectedPreset={selectedPreset}
          selectedComparePreset={selectedComparePreset}
          selectedYear={selectedYear}
          selectedCompareYear={selectedCompareYear}
          onPresetChange={handlePresetChange}
          onComparePresetChange={handleComparePresetChange}
          onYearChange={handleYearChange}
          onCompareYearChange={handleCompareYearChange}
          onDateRangeChange={handleDateRangeChange}
          onCompareToRangeChange={handleCompareToRangeChange}
          onApplyFilter={handleApplyFilter}
          onRefresh={() => actions.refetchAll({ dateRange, compareToRange })}
          isApplying={isApplyingPreset}
          disabled={loading}
        />

        {/* Global Error Display */}
        {hasErrors && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <h3 className="font-semibold text-red-800">Errors loading data:</h3>
            <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* KPI Dashboard */}
        <KPIDashboard
          dauData={state.dau.data}
          feedbackData={state.feedback.data}
          wellbeingData={state.wellbeing.data}
          compareToRange={compareToRange}
          loading={{
            dau: state.dau.loading,
            feedback: state.feedback.loading,
            wellbeing: state.wellbeing.loading
          }}
        />

        {/* ✅ Chart Sections - each replaces 100+ lines */}
      </div>
    </div>
  );
};

export default Reports;
